import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";
import { useNavigate } from "react-router-dom";
import "./TaskListDashboard.css";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LineChart,
  Line,
} from "recharts";

export default function TaskList() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState("");

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tasks/all`);
      setTasks(res.data.tasks || []);
    } catch (err) {
      console.error("Fetch Tasks Error:", err);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchTasks();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleView = (id) => navigate(`/admin/task/${id}`);
  const handleAssign = () => navigate("/admin/task/assign");

  const handleDelete = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      const res = await axios.delete(`${API_URL}/api/tasks/delete/${taskId}`);
      if (res.data.success) {
        alert("Task deleted!");
        fetchTasks();
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const downloadExcel = () => {
    const excelData = tasks.map((t) => ({
      Task: t.title,
      Client: t.clientId?.clientName || t.clientId?.leadName || "-",
      Project: t.projectId?.projectName || "-",
      AssignedTo: t.assignedTo?.map((e) => e.ename).join(", "),
      Status: t.status,
      Priority: t.priority,
      EstimatedTime: `${t.estimatedTime || 0} min`,
      TimeSpent: `${Math.floor((t.timeSpent || 0) / 60)} min`,
      StartDate: t.startDate ? new Date(t.startDate).toLocaleDateString() : "-",
      Deadline: t.dueDate ? new Date(t.dueDate).toLocaleDateString() : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tasks");
    XLSX.writeFile(wb, "Task_List.xlsx");
  };

  const statusData = [
    { name: "Completed", value: tasks.filter((t) => t.status === "Completed").length },
    { name: "Pending", value: tasks.filter((t) => t.status === "Pending").length },
    { name: "In Progress", value: tasks.filter((t) => t.status === "In Progress").length },
  ];

  const priorityData = [
    { name: "High", value: tasks.filter((t) => t.priority === "High").length },
    { name: "Medium", value: tasks.filter((t) => t.priority === "Medium").length },
    { name: "Low", value: tasks.filter((t) => t.priority === "Low").length },
  ];

  const timelineMap = tasks.reduce((acc, t) => {
    const key = t.startDate ? new Date(t.startDate).toLocaleDateString() : "No Date";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const timelineData = Object.keys(timelineMap).map((date) => ({
    date,
    count: timelineMap[date],
  }));

  const totalCompleted = tasks.filter((t) => t.status === "Completed").length;
  const totalPending = tasks.filter((t) => t.status !== "Completed").length;

  const filteredTasks = tasks.filter((task) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;

    const fields = [
      task.title,
      task.clientId?.clientName,
      task.clientId?.leadName,
      task.projectId?.projectName,
      task.status,
      task.priority,
    ];

    return fields.some((field) => String(field || "").toLowerCase().includes(q));
  });

  const COLORS = ["#14b8a6", "#f59e0b", "#f97316"];

  return (
    <div className="task-list-page">
      <div className="task-list-shell">
        <div className="task-list-hero">
          <div>
            <p className="task-list-eyebrow">Work Management</p>
            <h5>Task List Dashboard</h5>
            <span>Monitor status, priorities, and assignment flow in real time</span>
          </div>

          <div className="task-hero-actions">
            <button onClick={downloadExcel} className="task-btn ghost">
              Export Excel
            </button>
            <button onClick={handleAssign} className="task-btn primary">
              Add Task
            </button>
          </div>
        </div>

        <div className="task-kpi-grid">
          <div className="task-kpi-card task-kpi-total">
            <p>Total Tasks</p>
            <h3>{tasks.length}</h3>
          </div>

          <div className="task-kpi-card task-kpi-completed">
            <p>Completed</p>
            <h3>{totalCompleted}</h3>
          </div>

          <div className="task-kpi-card task-kpi-pending">
            <p>Pending</p>
            <h3>{totalPending}</h3>
          </div>
        </div>

        <div className="task-chart-grid">
          <div className="task-chart-card">
            <h6>Task Status</h6>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" innerRadius={38} outerRadius={76}>
                  {statusData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="task-chart-card">
            <h6>Priority Mix</h6>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={priorityData}>
                <XAxis dataKey="name" stroke="#475569" />
                <YAxis stroke="#475569" allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#0f766e" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="task-chart-card">
            <h6>Timeline</h6>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={timelineData}>
                <XAxis dataKey="date" stroke="#475569" />
                <YAxis stroke="#475569" allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#ea580c" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="task-search-card">
          <input
            type="text"
            placeholder="Search by task, client, project, status, priority"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="task-search-input"
          />
          {search && (
            <button type="button" className="task-clear-btn" onClick={() => setSearch("")}
            >
              Clear
            </button>
          )}
        </div>

        <div className="task-table-card">
          <div className="table-responsive">
            <table className="table text-center align-middle mb-0">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredTasks.length > 0 ? (
                  filteredTasks.map((task) => (
                    <tr key={task._id}>
                      <td>{task.title}</td>
                      <td>{task.clientId?.clientName || task.clientId?.leadName || "-"}</td>
                      <td>{task.projectId?.projectName || "-"}</td>
                      <td>
                        <span className={`task-chip status-${String(task.status || "").toLowerCase().replace(/\s+/g, "-")}`}>
                          {task.status}
                        </span>
                      </td>
                      <td>
                        <span className={`task-chip priority-${String(task.priority || "").toLowerCase()}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td>
                        <button className="task-action-btn view" onClick={() => handleView(task._id)}>
                          View
                        </button>
                        <button className="task-action-btn delete" onClick={() => handleDelete(task._id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="task-empty-row">
                      No tasks found for the current search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
